import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import { useTeacherNavigation } from "../contexts/TeacherNavigationContext";
import { TEACHER_DASHBOARD_NAVIGATION_IDS } from "../config/teacherDashboardNavigation";
import { getEntityTags, whoami } from "../services/APIClient";
import useReportingConfiguration from "../hooks/useReportingConfiguration";
import "./Reporting.css";

const REPORT_OPTIONS = [
  {
    id: "new-class-insights",
    to: "/enhanced-ai-reports?report=new_class_insights",
    icon: InfoOutlinedIcon,
    navigationId: TEACHER_DASHBOARD_NAVIGATION_IDS.NEW_CLASS_INSIGHTS,
    requiresTeacherAccess: true,
  },
  {
    id: "screening-point-report",
    to: "/enhanced-ai-reports?report=screening_point",
    icon: FactCheckOutlinedIcon,
    navigationId: TEACHER_DASHBOARD_NAVIGATION_IDS.SCREENING_POINT_REPORT,
    requiresTeacherAccess: true,
  },
  {
    id: "parent-teacher-meeting",
    to: "/enhanced-ai-reports?report=ptm_parent_handout",
    icon: GroupsOutlinedIcon,
    navigationId: TEACHER_DASHBOARD_NAVIGATION_IDS.PARENT_TEACHER_MEETING,
    requiresTeacherAccess: true,
  },
  {
    id: "end-of-year-report-assistance",
    to: "/eoy-report-assistant",
    icon: DescriptionOutlinedIcon,
    navigationId: TEACHER_DASHBOARD_NAVIGATION_IDS.EOY_REPORT_ASSISTANT,
    requiresTeacherAccess: true,
  },
  {
    id: "classroom-observations",
    to: "/senco/handover",
    icon: SwapVertRoundedIcon,
    navigationId: TEACHER_DASHBOARD_NAVIGATION_IDS.CLASSROOM_HANDOVER,
    requiresTeacherAccess: false,
  },
];

function Reporting() {
  const { isNavigationEnabled } = useTeacherNavigation();
  const reportingConfiguration = useReportingConfiguration();
  const [teacherName, setTeacherName] = useState("");
  const [teacherId, setTeacherId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const [hasEnhancedReportAccess, setHasEnhancedReportAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    whoami()
      .then((response) => {
        const stakeholder = response?.data?.stakeholder;
        if (!stakeholder || cancelled) return null;
        setTeacherName(stakeholder.name || "");
        setTeacherId(stakeholder.id || null);
        setSchoolId(stakeholder.schoolid || null);
        return getEntityTags("teacher", stakeholder.id);
      })
      .then((tags) => {
        if (!tags || cancelled) return;
        const tagList = Array.isArray(tags) ? tags : tags?.data || [];
        const accessTag = tagList.find(
          (tag) => tag.tag_definition?.tag_name === "chatbot_teacher_access"
        );
        setHasEnhancedReportAccess(accessTag?.tag_value === "ON");
      })
      .catch((error) => {
        console.error("Failed to load Reporting access:", error);
      })
      .finally(() => {
        if (!cancelled) setAccessLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const availableReports = useMemo(
    () =>
      REPORT_OPTIONS.filter((report) => {
        return (
          isNavigationEnabled(report.navigationId) &&
          (!report.requiresTeacherAccess || hasEnhancedReportAccess)
        );
      }),
    [hasEnhancedReportAccess, isNavigationEnabled]
  );
  const pageCopy = reportingConfiguration[TEACHER_DASHBOARD_NAVIGATION_IDS.REPORTING];

  return (
    <Container fluid className="reporting-page-shell">
      <HeadingBanner
        name={teacherName ? `Welcome ${teacherName}!` : "Reporting"}
        isLoggedIn={true}
        cloud={true}
        schoolid={schoolId}
        teacherid={teacherId}
      />
      <Row className="reporting-page-row">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="reporting-page-content">
          <main className="reporting-content-inner">
            <header className="reporting-heading-block">
              <div className="reporting-title-row">
                <h1>{pageCopy.title}</h1>
                <span className="reporting-beta-badge">BETA</span>
              </div>
              <p>{pageCopy.subheading}</p>
            </header>

            <section className="reporting-options-panel" aria-label="Reporting tools">
              {accessLoading ? (
                <div className="reporting-loading" role="status">
                  <div className="spinner-border text-info" aria-hidden="true" />
                  <span>Loading reporting tools...</span>
                </div>
              ) : availableReports.length > 0 ? (
                <div className="reporting-options-list">
                  {availableReports.map((report) => {
                    const ReportIcon = report.icon;
                    const reportCopy = reportingConfiguration[report.navigationId];
                    return (
                      <Link
                        className="reporting-option-card"
                        key={report.id}
                        to={report.to}
                      >
                        <span className="reporting-option-icon" aria-hidden="true">
                          <ReportIcon />
                        </span>
                        <span className="reporting-option-label">{reportCopy.title}</span>
                        <span className="reporting-option-action" aria-hidden="true">
                          <ArrowForwardIosRoundedIcon />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="reporting-empty-state" role="status">
                  <h2>No reporting tools are currently available</h2>
                  <p>Please check back later.</p>
                </div>
              )}
            </section>
          </main>
        </Col>
      </Row>
    </Container>
  );
}

export default Reporting;
